import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";

import { AgGridVue } from "ag-grid-vue";
import Vue from "vue";
import router from '../../../router'
import moduleDrilling from "../store/moduleDrilling.js";
import Datepicker from 'vuejs-datepicker';
import DailyReportActionsCellRenderer from "./components/DailyReportActionsCellRenderer.vue"
import service from "./stagingDailyReportService.js"

import featureFlagsService from '@/app/shared/services/featureFlagsService.js'
import features from "@/app/shared/AppFeatures.js"

export default {
  components: {
    /* eslint-disable vue/no-unused-components */
    AgGridVue,
    Datepicker,
    DailyReportActionsCellRenderer
  },
  data() {
    return {
      selectedDateTime: null,
      selectedMainProjectId: null,
      selectedDailyReportId: null,
      showDeleted: false,
      importationResults: [],
      importationPopupActive: false,
      unimportedDates: {},
      model: [],

      // grid attributes
      defaultColDef: {
        sortable: true,
        resizable: true,
        editable: false
      },
      gridApi: null,
      gridOptions: {
        getRowNodeId: (data) => {
          return data.stagingDailyReportId;
        }
      },
      context: { componentParent: this },
      rowClassRules: {
        'dailyReport-approved': 'data.isImported  && !data.isDeleted',
        'dailyReport-modified': '!data.isImported && data.isModified && !data.isDeleted',
        'dailyReport-deleted': 'data.isDeleted'
      },
      columnDefs: [
        {
          headerName: 'Actions',
          field: 'stagingDailyReportId',
          width: 150,
          editable: false,
          sortable: false,
          checkboxSelection: true,
          headerCheckboxSelectionFilteredOnly: true,
          headerCheckboxSelection: true,
          cellRendererFramework: Vue.extend(DailyReportActionsCellRenderer)
        },
        {
          headerName: this.$t('Drilling.Drills.Name'),
          field: "drillCode",
          filter: true,
          width: 150,
          cellClassRules: {
            'rag-red': "!ctx.componentParent.drillExists(data)"
          }
        },
        {
          headerName: this.$t('Drilling.Contracts.Name'),
          field: "contractNumber",
          filter: true,
          width: 165,
          cellClassRules: {
            'rag-red': "typeof ctx.componentParent.contracts.find(l => l.code === data.contractNumber) === 'undefined'"
          }
        },
        {
          headerName: this.$t('Drilling.Projects.SingleTitle'),
          field: "projectCode",
          filter: true,
          width: 165,
          valueGetter: (params) => {
            return params.context.componentParent.getProjectLabel(params.data);
          },
          cellClassRules: {
            'rag-red': "ctx.componentParent.findProject(data) === undefined"
          }
        },
        {
          headerName: this.$t('Drilling.DailyReports.Vendor'),
          field: "vendor",
          filter: true,
          width: 165
        },
        {
          headerName: this.$t('Drilling.JobEntries.Hours'),
          field: "totalHours",
          filter: true,
          width: 135
        },
        {
          headerName: this.$t('Drilling.DrillEntries.MetersDrilled'),
          field: "totalMetersDrilled",
          filter: true,
          width: 185,
          valueFormatter: (params) => {
            return params.value.toFixed(1);
          }
        },
        {
          headerName: this.$t('Drilling.Holes.Name'),
          field: "holes",
          filter: true,
          width: 200,
          valueFormatter: (params) => {
            return params.value.join(', ');
          }
        }
      ],
      components: {
        DailyReportActionsCellRenderer
      },
      employeesTimesheets: []
    };
  },

  computed: {
    drills() {
      return this.$store.state.drilling.drills;
    },
    companies() {
      return this.$store.state.drilling.companies;
    },
    contracts() {
      return this.$store.state.drilling.contracts.filter(c => c.mainProjectId === this.selectedMainProjectId);
    },
    projects() {
      return this.$store.state.drilling.projects;
    },
    friendlyDate() {
      if (this.selectedDateTime)
        return this.$options.filters.toDate(this.selectedDateTime);
      else
        return '';
    },
  },
  watch: {
    selectedDateTime(val) {
      if (!val)
        return;
      this.$refs.listCard.refreshcard();
    },
    showDeleted() {
      if (!this.selectedDateTime) return;

      this.$refs.listCard.refreshcard();
    }
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
  },
  created() {
    if (!moduleDrilling.isRegistered) {
      this.$store.registerModule("drilling", moduleDrilling);
      moduleDrilling.isRegistered = true;
    }

    // watched here rather than in `watch` because the drilling store module only exists once registered above;
    // rows may be rendered before projects are loaded, so re-resolve project names
    this.$watch('projects', () => {
      this.gridApi?.refreshCells({ columns: ['projectCode'], force: true });
    });

    this.selectedMainProjectId = Number(this.$store.state.projectId);

    // load secondary data, then define default data
    this.$vs.loading();
    Promise.all([
      this.$store.dispatch("drilling/fetchAllDrills"),
      this.$store.dispatch("drilling/fetchAllProjects", this.selectedMainProjectId),
      this.$store.dispatch("drilling/fetchAllContracts"),
      this.$store.dispatch("drilling/fetchAllCategories"),
      this.$store.dispatch("drilling/fetchAllCompanies"),
      this.$store.dispatch("drilling/fetchAllEmployeeTitles"),
      this.$store.dispatch("drilling/fetchAllCoreBarrels"),
      this.$store.dispatch("drilling/fetchAllInstruments"),
      this.defineNonImportedDates(),
      this.defineLatestImportedDate()
    ]).finally(() => {
      this.$vs.loading.close();
    })
  },
  methods: {
    // projectId takes priority over projectCode, matching the Vue3 frontend's getPropertyLabel
    findProject(data) {
      if (data.projectId) {
        return this.$store.getters['drilling/getProjectById'](data.projectId);
      }
      return this.$store.getters['drilling/getProjectByCode'](data.projectCode);
    },
    getProjectLabel(data) {
      return this.findProject(data)?.name ?? data.projectCode;
    },
    drillExists(drill) {
      const contract = this.contracts.find(l => l.code === drill.contractNumber);
      const company = this.companies.find(_ => _.companyId === contract.companyId)
      return company.drillRigs.find(d => d.code === drill.drillCode) !== undefined;
    },
    addNewData() {
      router.push({ name: 'drilling-staging-add', params: { date: this.selectedDateTime } });
    },
    editData(dailyReport) {
      router.push({ name: 'drilling-staging-edit', params: { stagingDailyReportId: dailyReport.stagingDailyReportId } });
    },
    viewData(dailyReport) {
      router.push({ name: 'drilling-staging-view', params: { stagingDailyReportId: dailyReport.stagingDailyReportId } });
    },
    closeAndEditData(dailyReport) {
      this.importationPopupActive = false;
      setTimeout(() => {
        router.push({ name: 'drilling-staging-edit', params: { stagingDailyReportId: dailyReport.stagingDailyReportId, importationResults: this.importationResults } });
      }, 200);
    },
    //#region Data Fetch
    defineNonImportedDates() {
      return new Promise((resolve) => {
        service.getNonImportedDates(this.selectedMainProjectId, this)
          .then((response) => {
            const dates = response.data.model.map(date => new Date(date));
            this.unimportedDates = { dates };
            resolve(response)
          })
      });
    },
    defineLatestImportedDate() {
      return new Promise((resolve) => {
        if (this.$route.params.date) {
          this.selectedDateTime = new Date(this.$route.params.date);
          resolve();
          return;
        }
        service.getLatestImportedDate(this.selectedMainProjectId, this)
          .then((response) => {
            this.selectedDateTime = new Date(response.data.model);
            resolve(response)
          })
      });
    },
    initializeData() {
      return Promise.all([
        this.getDailyReportEntries(),
        this.getEmployeesTimesheets()
      ]);
    },
    getDailyReportEntries() {
      if (this.selectedDateTime) {
        const d = new Date(this.selectedDateTime);
        return service.fetchByDate(this.selectedMainProjectId, d.toDateString(), this.showDeleted, this)
          .then((response) => {
            this.model = response.data.model;
          }).finally(() => {
            this.$refs.listCard.removeRefreshAnimation()
          });
      }
    },
    getEmployeesTimesheets() {
      if (this.selectedDateTime) {
        const d = new Date(this.selectedDateTime);
        return service.fetchEmployeesTimesheets(this.selectedMainProjectId, d.toDateString(), this.showDeleted, this)
          .then((response) => {
            this.employeesTimesheets = response.data.model;
          }).finally(() => {
            this.$refs.listCard.removeRefreshAnimation()
          });
      }
    },


    //#endregion

    //#region Delete

    deleteData(id) {
      this.$vs.loading();
      return service.delete(id, this)
        .then(() => {
          this.$notifyDeleteSuccess(this.$t("Drilling.DailyReports.DailyReportDeleted"));
        }).finally(() => {
          this.$vs.loading.close();
          this.$refs.listCard.refreshcard();
        });
    },

    restoreReport(report) {
      this.$vs.loading();
      return service.restore(report.stagingDailyReportId, this)
        .then(() => {
          this.$notifySuccess(this.$t("Drilling.DailyReports.RestoreCompleted"));
        })
        .finally(() => {
          this.$vs.loading.close();
          this.$refs.listCard.refreshcard();
        });
    },

    openDeleteConfirm() {
      const rows = this.gridApi.getSelectedRows();
      if (rows.length === 0) {
        this.$vs.dialog({
          color: 'danger',
          title: this.$t("Delete"),
          text: this.$t('Drilling.DailyReports.NoDailyReportSelected')
        })
      }
      else {
        let text = this.$t('Drilling.DailyReports.ConfirmDeleteMultiple');
        if (rows.some(r => r.isImported)) text = this.$t('Drilling.DailyReports.ConfirmDeleteMultipleImported')
        this.$deleteConfirmation({
          text,
          accept: this.deleteBatch
        });
      }
    },

    deleteBatch() {
      const rows = this.gridApi.getSelectedRows();

      this.$vs.loading();
      const ids = rows.map(t => t.stagingDailyReportId);
      return service.deleteBatch(ids, this)
        .then(() => {
          this.$notifySuccess(this.$t("Drilling.DailyReports.DailyReportDeleted"));
        }).finally(() => {
          this.$vs.loading.close();
          this.$refs.listCard.refreshcard();
        });
    },

    //#endregion

    openCancelImportConfirm() {
      const rows = this.gridApi.getSelectedRows();
      if (rows.length === 0) {
        this.$vs.dialog({
          color: 'danger',
          title: this.$t("Import"),
          text: this.$t('Drilling.DailyReports.NoReportSelected')
        })
      }
      else {
        this.$vs.dialog({
          type: "confirm",
          color: "warning",
          title: this.$t("Import"),
          text: this.$t('Drilling.DailyReports.ConfirmCancelImportBatch'),
          accept: this.cancelImportBatch
        });
      }
    },
    cancelImportBatch() {
      const rows = this.gridApi.getSelectedRows();
      this.$vs.loading();
      const ids = rows.map(l => l.stagingDailyReportId);
      service.cancelImportBatch(ids, this).then(() => {
        this.$notifySuccess(this.$t("Drilling.DailyReports.CancelImportCompletedBatch"));
      }).finally(() => {
        this.$vs.loading.close();
        this.$refs.listCard.refreshcard();
      });
    },
    cancelImportation(report) {
      this.$vs.loading();
      service.cancelImportBatch([report.stagingDailyReportId], this).then(() => {
        this.$notifySuccess(this.$t("Drilling.DailyReports.CancelImportCompleted"));
      }).finally(() => {
        this.$vs.loading.close();
        this.$refs.listCard.refreshcard();
      });
    },
    importBatch() {
      const rows = this.gridApi.getSelectedRows();
      if (rows.length === 0) {
        this.$vs.dialog({
          color: 'danger',
          title: this.$t("Import"),
          text: this.$t('Drilling.DailyReports.NoReportSelected')
        })
        return;
      }
      this.$vs.loading();
      const ids = rows.map(l => l.stagingDailyReportId);
      service.importBatch(ids, this).then((response) => {
        this.importationResults = response.data.model.results;
        this.importationPopupActive = true;
      }).finally(() => {
        this.$vs.loading.close();
        this.$refs.listCard.refreshcard();
      });
    }
  }
};
